import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Stack } from '@mui/material';
import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid';

import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';

import { GetFlightCampaignsData, createFlightCampaign, deleteFlightCampaign } from '~/services/FlightsService';

import { FLIGHT_CAMPAIGNS_QUERY_KEY, useFlightCampaigns } from '../hooks/useFlightCampaigns';

import { FlightsCampaignsForm, FormState } from './FlightsCampaignsForm';
import { FlightsCampaignsTable } from './FlightsCampaignsTable';

export function FlightsCampaings() {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<GridFilterModel>();
  const [activeOnly, setActiveOnly] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const queryClient = useQueryClient();
  const debouncedFilters = useDebounced(filters, 400);
  const filterState = useMemo(() => getFilterState(debouncedFilters), [debouncedFilters]);
  const options = useMemo(() => {
    return {
      page: page + 1,
      pageSize: 20,
      active: activeOnly,
      ...(filterState.filters && { filters: filterState.filters }),
    };
  }, [activeOnly, filterState.filters, page]);
  const {
    error: fetchingDataError,
    loading: loadingData,
    data,
    total,
  } = useFlightCampaigns(options, filterState.isComplete);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFlightCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGNS_QUERY_KEY] });
      enqueueSnackbar('Campaign was deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: (formData: FormState) =>
      createFlightCampaign({
        brand: formData.brand.join(','),
        type: formData.type,
        title: formData.title,
        region: formData.region,
        header: formData.header,
        description: formData.description,
        start_date: formData.startDate,
        end_date: formData.endDate,
        hero_image_public_id: formData.heroImage,
        terms: formData.terms,
        airline_code: formData.airlineCode,
        price: parseFloat(formData.price),
        fare_class: formData.fareClass,
        fare_type: formData.fareType,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGNS_QUERY_KEY] });
      enqueueSnackbar('Campaign was created', { variant: 'success' });
      push(`/flights/campaigns/${response.result.id}`);
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = (formData: FormState) => {
    createMutation.mutate(formData);
  };

  const handleFilterChange = useCallback((model: GridFilterModel) => {
    setPage(0);
    setFilters(model);
  }, []);

  const handleActiveOnlyChange = useCallback((activeOnly: boolean) => {
    setPage(0);
    setActiveOnly(activeOnly);
  }, []);

  return (
    <Stack gap={6}>
      <FlightsCampaignsForm
        onSubmit={handleSubmit}
        error={createMutation.isError ? createMutation.error?.message : undefined}
        loading={createMutation.isPending}
      />
      <FlightsCampaignsTable
        data={data}
        total={total}
        page={page}
        onDelete={handleDelete}
        error={fetchingDataError?.message}
        loading={loadingData}
        activeOnly={activeOnly}
        onActiveOnlyChange={handleActiveOnlyChange}
        activeFilters={filters}
        onFilterChange={handleFilterChange}
        onPageChange={setPage}
      />
    </Stack>
  );
}

type CampaignFilterColumn = keyof NonNullable<GetFlightCampaignsData['filters']>;

const FILTER_COLUMNS: ReadonlySet<CampaignFilterColumn> = new Set([
  'id',
  'title',
  'type',
  'header',
  'region',
  'start_date',
  'end_date',
  'brand',
]);

function getFilterState(filterModel?: GridFilterModel): {
  filters?: NonNullable<GetFlightCampaignsData['filters']>;
  isComplete: boolean;
} {
  const items = filterModel?.items ?? [];
  if (items.length === 0) {
    return { isComplete: true };
  }

  const filters = items.map(getCampaignFilter);
  if (!filters.every((filter): filter is NonNullable<typeof filter> => filter !== undefined)) {
    return { isComplete: false };
  }

  return { isComplete: true, filters: Object.fromEntries(filters) };
}

function getCampaignFilter(item: GridFilterItem) {
  const column = item.field as CampaignFilterColumn;
  if (!item.field || !FILTER_COLUMNS.has(column)) {
    return;
  }

  const value = item.value instanceof Date ? item.value.toISOString() : item.value;
  if (typeof value !== 'string' || value === '') {
    return;
  }

  return [column, value] as const;
}
